import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateContentCard from '../../utils/content/getOrCreateContentCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import setContentStrategyTool from './setContentStrategyTool';
import setContentFeedTool from './setContentFeedTool';
import setContentSetTool from './setContentSetTool';
import setSpecificContentTool from './setSpecificContentTool';
import searchContentFeedColumnsTool from './searchContentFeedColumnsTool';
import searchContentSignalsTool from './searchContentSignalsTool';
import setMatchingConditionsTool from './setMatchingConditionsTool';
const cardSpecificInstructions = `### What is a Content card
A Content card selects a single content item from a content feed to inject into the ad context. Content items define dynamic parameters for the creative template like headline, CTA, color, or background image. The fallback output is selected when the strategy fails to select a content item.

### Content card rules
- The selected content set must belong to the selected content feed.

### Property Dependencies
- The content feed must be set before: the content set (which must belong to the feed), specific content items (validated against the feed), and feed column searches (which query the feed's schema).
- The strategy (Specific vs Matching) determines which properties are relevant:
  - Specific strategy: requires a specific content item, which depends on the feed.
  - Matching strategy: requires matching conditions, which depend on both feed columns and signals being searched first.
- Feed columns and content signals can be searched independently of each other, but both must be searched before matching conditions can be configured.`;

export default function addOrUpdateContentCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ContentCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateContentCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateContentCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CONTENT_FEED]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CONTENT_FEED,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Content tools
        setContentStrategyTool(),
        setContentFeedTool(),
        setContentSetTool(),
        setSpecificContentTool(),
        searchContentFeedColumnsTool(),
        searchContentSignalsTool(store),
        setMatchingConditionsTool(store),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateContentCard,
    displayName: 'Working on content card',
    description:
      'Add or update a content selection card. The card selects a single content item from content feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure content selection strategy, configure matching conditions for matching strategy, assign a feed, ' +
      'assign a content set, or assign a specific content item for the specific items strategy.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
